// Static config for the session-type design previews.
//
// These power the not-yet-wired "Coaching Session", "Protocol Development" and
// "Executive Meeting" screens (see pages/SessionPreview.js). Sample deliverable
// content is illustrative only — it shows reviewers what each generated output
// would look like. The "Patient Encounter" entry is LIVE and links into the
// real scribe flow; it is included here only so the hub can list all four.
import {
  MonitorHeart as EncounterIcon,
  Psychology as CoachingIcon,
  Science as ProtocolIcon,
  Groups as ExecutiveIcon,
  Description as SummaryIcon,
  Flag as GoalsIcon,
  CheckCircle as ActionIcon,
  Gavel as DecisionIcon,
  TipsAndUpdates as RecommendIcon,
  HelpOutline as QuestionIcon,
  Assignment as TaskIcon,
  EventNote as MinutesIcon,
} from '@mui/icons-material';

// Live session type — handled by the existing ScribeSession flow.
export const PATIENT_ENCOUNTER = {
  key: 'encounter',
  name: 'Patient Encounter',
  icon: EncounterIcon,
  status: 'live',
  tagline: 'Clinical visit captured into a SOAP note, summary and care plan.',
  deliverableNames: ['SOAP Note', 'Visit Summary', 'Care Plan', 'Action Items'],
  startPath: '/patients', // pick a patient to begin the real recording flow
};

// The three design-preview session types, keyed by the :type route param.
export const SESSION_PREVIEWS = {
  coaching: {
    key: 'coaching',
    name: 'Coaching Session',
    icon: CoachingIcon,
    status: 'preview',
    tagline: 'Health & wellness coaching, turned into goals and action items.',
    contextLabel: 'Client',
    context: {
      title: 'Jordan Mae',
      subtitle: 'Wellness coaching • Session 4 of 12 • Focus: sleep & stress',
    },
    tips: [
      'Choose a quiet space — the AI separates coach and client voices',
      'Name specific goals out loud so they are captured cleanly',
      'Summaries and action items are generated for client follow-up',
    ],
    deliverables: [
      {
        title: 'Session Summary',
        icon: SummaryIcon,
        kind: 'paragraph',
        body:
          'Jordan reported better adherence to the evening wind-down routine, with 2 of 5 weeknights screen-free. Stress remains elevated around work deadlines. We explored a 10-minute morning breathwork practice and reframed “all-or-nothing” thinking about exercise. Energy and mood trending up since last session.',
      },
      {
        title: 'Goals',
        icon: GoalsIcon,
        kind: 'bullets',
        body: [
          'Screen-free wind-down on at least 4 weeknights',
          'Morning breathwork 10 minutes, 5 days/week',
          'Two 20-minute walks during the work day',
        ],
      },
      {
        title: 'Action Items',
        icon: ActionIcon,
        kind: 'checks',
        body: [
          'Client: set a nightly 9:30pm wind-down alarm',
          'Client: log sleep in the app for two weeks',
          'Coach: send the breathwork audio guide',
          'Coach: check in by message mid-week',
        ],
      },
    ],
  },

  protocol: {
    key: 'protocol',
    name: 'Protocol Development',
    icon: ProtocolIcon,
    status: 'preview',
    tagline: 'Clinical discussion captured into a protocol draft with decisions.',
    contextLabel: 'Topic',
    context: {
      title: 'Adrenal Support Protocol — v5 draft',
      subtitle: 'Participants: Dr. Rao, Dr. Lin, NP Carter • Working session',
    },
    tips: [
      'State the protocol name and version at the start',
      'Call out decisions and rationale explicitly',
      'Open questions are captured separately for follow-up',
    ],
    deliverables: [
      {
        title: 'Protocol Draft',
        icon: SummaryIcon,
        kind: 'paragraph',
        body:
          'Adrenal Support Protocol (v5). Phase 1 (weeks 0–4): foundational sleep, blood-sugar stabilization and adaptogen introduction. Phase 2 (weeks 4–8): targeted micronutrient repletion based on labs. Phase 3 (weeks 8–12): taper and reassessment. Eligibility limited to patients with confirmed labs and no contraindicated medications.',
      },
      {
        title: 'Decisions',
        icon: DecisionIcon,
        kind: 'bullets',
        body: [
          'Adopt a 3-phase structure over the prior 2-phase model',
          'Require baseline cortisol panel before Phase 1',
          'Standardize adaptogen dosing across providers',
        ],
      },
      {
        title: 'Recommendations',
        icon: RecommendIcon,
        kind: 'bullets',
        body: [
          'Add a patient-facing one-pager for Phase 1',
          'Pilot with 10 patients before clinic-wide rollout',
        ],
      },
      {
        title: 'Open Questions',
        icon: QuestionIcon,
        kind: 'bullets',
        body: [
          'Reassessment cadence for Phase 3 — 2 vs 4 weeks?',
          'How to handle patients already mid-protocol on v4?',
        ],
      },
    ],
  },

  executive: {
    key: 'executive',
    name: 'Executive Meeting',
    icon: ExecutiveIcon,
    status: 'preview',
    tagline: 'Leadership meeting captured into minutes, decisions and tasks.',
    contextLabel: 'Meeting',
    context: {
      title: 'Q3 Operations Review',
      subtitle: 'Attendees: Practice Director, Clinical Lead, Ops Manager',
    },
    tips: [
      'Identify attendees at the start for accurate attribution',
      'State decisions and task owners clearly',
      'Minutes, decisions and assigned tasks are generated separately',
    ],
    deliverables: [
      {
        title: 'Meeting Minutes',
        icon: MinutesIcon,
        kind: 'paragraph',
        body:
          'Reviewed Q3 patient volume (up 14% QoQ) and onboarding throughput. Wait times for new-patient appointments remain above target. Discussed adding evening coaching slots and a second front-desk hire. Marketing spend reallocated toward referral partnerships. Next review scheduled for early Q4.',
      },
      {
        title: 'Decisions',
        icon: DecisionIcon,
        kind: 'bullets',
        body: [
          'Open two evening coaching slots starting next month',
          'Approve a part-time front-desk hire',
          'Shift 20% of ad budget to referral partnerships',
        ],
      },
      {
        title: 'Assigned Tasks',
        icon: TaskIcon,
        kind: 'checks',
        body: [
          'Ops Manager: post the front-desk role by Friday',
          'Clinical Lead: draft the evening coaching schedule',
          'Director: brief referral partners on the new program',
        ],
      },
    ],
  },
};

export const PREVIEW_LIST = Object.values(SESSION_PREVIEWS);
